import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const COLS = ["MAS", "PSA", "Beckett", "JSA"];

// true = offered, false = not a core focus / varies. Conservative and defensible.
const ROWS = [
  { f: "Instant online cert verification", v: [true, true, true, true] },
  { f: "Tamper-evident sealed slab", v: [true, true, true, false] },
  { f: "Live public population registry", v: [true, true, true, false] },
  { f: "Autograph authentication", v: [true, true, true, true] },
  { f: "On-site witnessed grading at conventions", v: [true, false, false, false] },
  { f: "Same-day pickup with SMS & email alerts", v: [true, false, false, false] },
  { f: "Watch your card graded in person", v: [true, false, false, false] },
];

const FAQ = [
  ["Is MAS Grading legitimate?", "Yes. MAS Grading is operated by MAS Certified, Inc. Every card is sealed in a tamper-evident slab with a unique certification number that anyone can verify online in seconds, and every grade is tracked in a transparent, public population registry."],
  ["How do I verify a MAS certification?", "Enter the certification number at grading.mascertified.com/lookup. You'll instantly see the card details, grade, and live population data — no account required."],
  ["What makes MAS different from PSA, Beckett, and JSA?", "MAS pairs the fundamentals collectors expect — tamper-evident slabs, a public population registry, and instant online verification — with witnessed, on-site grading at conventions, where you can watch your cards be graded and pick them up the same day with real-time SMS and email alerts."],
];

const LD = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
];

export default function Compare() {
  return (
    <div className="bg-white" data-testid="compare-page">
      <Seo
        title="Best Card Grading Company? MAS vs PSA, Beckett & JSA"
        description="Compare MAS Grading with PSA, Beckett, and JSA. See how MAS pairs tamper-evident slabs, a public population registry, and instant verification with witnessed, on-site convention grading."
        path="/compare"
        jsonLd={LD}
      />
      <PageHeader
        eyebrow="Compare"
        title="MAS vs PSA, Beckett & JSA"
        subtitle="An honest look at how MAS Grading stacks up. We deliver the fundamentals collectors expect — and add transparency and in-person grading the mail-in giants don't."
        word="VS"
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <div className="overflow-x-auto rounded-3xl border border-neutral-200">
          <table className="w-full min-w-[640px] text-left" data-testid="compare-table">
            <thead>
              <tr className="bg-[#0A0A0A] text-white">
                <th className="py-5 px-6 font-display font-bold text-sm">Feature</th>
                {COLS.map((c, i) => (
                  <th key={c} className={`py-5 px-6 font-display font-extrabold text-center ${i === 0 ? "text-white" : "text-white/70 font-bold text-sm"}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, ri) => (
                <tr key={r.f} className={ri % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                  <td className="py-4 px-6 text-neutral-700 text-sm font-medium">{r.f}</td>
                  {r.v.map((on, ci) => (
                    <td key={ci} className={`py-4 px-6 text-center ${ci === 0 ? "bg-neutral-50" : ""}`}>
                      {on ? <Check className="inline text-[#0A0A0A]" size={18} /> : <Minus className="inline text-neutral-300" size={18} />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-neutral-400 mt-4">Comparison reflects each company's primary, publicly offered services and may change. Feature availability can vary by company, product line, and event — always confirm current offerings directly with each provider.</p>

        <div className="mt-16">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-[#0A0A0A]">Frequently asked</h2>
          <div className="mt-8 space-y-6">
            {FAQ.map(([q, a]) => (
              <div key={q} className="rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-6 md:p-8">
                <h3 className="font-display font-bold text-lg text-[#0A0A0A]">{q}</h3>
                <p className="text-neutral-500 mt-2 leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/submit" data-testid="compare-cta-submit" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300">
            Submit an Item <ArrowRight size={16} />
          </Link>
          <Link to="/witnessed-certification" data-testid="compare-cta-witnessed" className="inline-flex items-center gap-2 px-8 py-4 border border-neutral-300 text-[#0A0A0A] font-bold rounded-full hover:border-[#0A0A0A] transition-colors duration-300">
            What is witnessed certification?
          </Link>
        </div>
      </section>
    </div>
  );
}
